import { envConfig } from "@configs/env";
import { ACCESS_TOKEN_ENDPOINT } from "@constants";
import { BaseQueryApi } from "@reduxjs/toolkit/query";
import { RootState } from "@stores";
import { isTokenExpired } from "@utils/jwt";
import logger from "@utils/logger";
import { getMarketingData } from "@utils/marketing-data";
import { formatDisplayVersion } from "@utils/misc";
import { API_HEADERS } from "kz-ui-sdk";

const MP_VERSION_HEADER = "mpversion";

type HeaderPreparer = (
  header: Headers,
  api?: Pick<BaseQueryApi, "getState" | "extra" | "endpoint" | "type" | "forced">,
  state?: RootState,
) => void;

// Headers.set throws on characters above 255 or line breaks, which would fail every API call
const SAFE_HEADER_VALUE = /^[\t\x20-\x7E\xA0-\xFF]+$/;

/**
 * Attach marketing data to header
 * @description Attach marketing data (mktg-data, in-memory fallback) to header.
 * Don't read the current URL here: `url` must stay the landing href.
 */
export const prepareMarketingData: HeaderPreparer = (header): void => {
  const mktgData = getMarketingData();
  if (mktgData) {
    for (const [key, value] of Object.entries(mktgData)) {
      if (value && SAFE_HEADER_VALUE.test(value)) {
        header.set(key, value);
      }
    }
  }
};

/**
 * Attach access token to header
 * @description Attach access token to header if it's not expired and not in the access token endpoint
 */
export const prepareAccessToken: HeaderPreparer = (header, api, state) => {
  if (
    state?.auth.oauth?.access_token &&
    !isTokenExpired(state.auth.oauth?.access_token) &&
    api?.endpoint !== ACCESS_TOKEN_ENDPOINT
  ) {
    header.set(API_HEADERS.AUTHORIZATION, `Bearer ${state.auth.oauth.access_token}`);
  } else {
    logger._console.log("Header is not set", api, state);
  }
};

/**
 * Attach account id to header
 * @description Attach account id to header
 */
export const prepareAccountId: HeaderPreparer = (header) => {
  header.set(API_HEADERS.ACCOUNT_ID, envConfig.accountId);
};

/**
 * Attach MP version to header
 * @description Attach the build version so BE can record it on audit log entries.
 * Omitted when unknown (local dev) rather than sending a synthesized timestamp.
 */
export const prepareAppVersion: HeaderPreparer = (header) => {
  const version = import.meta.env.VITE_APP_VERSION;
  if (version) {
    // Match the version shown in the AppVersion badge
    header.set(MP_VERSION_HEADER, formatDisplayVersion(version));
  }
};
